/* eslint-disable no-nested-ternary */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutline from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LightbulbOutlined from '@mui/icons-material/EmojiObjectsOutlined';
import MicOutlined from '@mui/icons-material/MicOutlined';
import MicOffOutlined from '@mui/icons-material/MicOffOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import VideocamOffOutlined from '@mui/icons-material/VideocamOffOutlined';
import { updateDevicePreferences } from '../deviceSettingsStorage';
import DeviceSettingsFields from './DeviceSettingsFields';
import { usePreCallMedia } from './usePreCallMedia';
import LiveCaptionsToggle from '../live_captions/LiveCaptionsToggle';

const SafeDialog = ({ open, onClose, children, className, testId }) => (
  <Dialog
    open={open}
    maxWidth={false}
    onClose={(event, reason) => {
      if (reason !== 'backdropClick') onClose();
    }}
    slotProps={{ paper: { className } }}
    data-testid={testId}
  >
    {children}
  </Dialog>
);

SafeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  testId: PropTypes.string.isRequired,
};

const CloseButton = ({ onClick, label }) => (
  <IconButton
    className="vonage-pre-call__close"
    onClick={onClick}
    aria-label={label}
  >
    <CloseIcon />
  </IconButton>
);

CloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const ModalButton = ({
  audience,
  tone,
  primary = false,
  onClick,
  children,
}) =>
  audience === 'practitioner' ? (
    <Button
      className={`flat-button-${tone}${
        primary ? ' vonage-pre-call__flat-primary' : ''
      }`}
      onClick={onClick}
    >
      {children}
    </Button>
  ) : (
    <button
      type="button"
      className={primary ? 'vonage-pre-call__primary' : undefined}
      onClick={onClick}
    >
      {children}
    </button>
  );

ModalButton.propTypes = {
  audience: PropTypes.oneOf(['client', 'practitioner']).isRequired,
  tone: PropTypes.oneOf(['green', 'white']).isRequired,
  primary: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const PermissionDialog = ({ t, onContinue, onDismiss, audience }) => (
  <SafeDialog
    open
    onClose={onDismiss}
    className="vonage-pre-call-dialog vonage-pre-call-dialog--small"
    testId="pre-call-permission-modal"
  >
    <DialogContent
      className={`vonage-pre-call__instruction vonage-pre-call__instruction--${audience}`}
    >
      <CloseButton onClick={onDismiss} label={t('preCall.close')} />
      <InfoOutlined className="vonage-pre-call__info-icon" />
      <h1>{t('preCall.permission.title')}</h1>
      <p>{t('preCall.permission.description')}</p>
      <div className="vonage-pre-call__permission-row">
        <MicOutlined />
        <span>
          <strong>{t('preCall.permission.microphone.title')}</strong>
          {t('preCall.permission.microphone.description')}
        </span>
      </div>
      <div className="vonage-pre-call__permission-row">
        <VideocamOutlined />
        <span>
          <strong>{t('preCall.permission.camera.title')}</strong>
          {t('preCall.permission.camera.description')}
        </span>
      </div>
      <ModalButton
        audience={audience}
        tone="green"
        primary
        onClick={onContinue}
      >
        {t(
          audience === 'practitioner'
            ? 'preCall.permission.practitionerContinue'
            : 'preCall.permission.continue',
        )}
      </ModalButton>
    </DialogContent>
  </SafeDialog>
);

PermissionDialog.propTypes = {
  t: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  audience: PropTypes.oneOf(['client', 'practitioner']).isRequired,
};

const RequiredDialog = ({ t, open, onClose, audience }) => {
  const practitioner = audience === 'practitioner';
  return (
    <SafeDialog
      open={open}
      onClose={onClose}
      className="vonage-pre-call-dialog vonage-pre-call-dialog--small"
      testId={
        practitioner
          ? 'pre-call-practitioner-required-modal'
          : 'pre-call-microphone-required-modal'
      }
    >
      <DialogContent
        className={`vonage-pre-call__instruction vonage-pre-call__instruction--${audience}`}
      >
        <CloseButton onClick={onClose} label={t('preCall.close')} />
        {practitioner ? (
          <ErrorOutline className="vonage-pre-call__info-icon is-critical" />
        ) : (
          <InfoOutlined className="vonage-pre-call__info-icon is-critical" />
        )}
        <h1>
          {t(
            practitioner
              ? 'preCall.practitionerRequired.title'
              : 'preCall.microphoneRequired.title',
          )}
        </h1>
        <p>
          {t(
            practitioner
              ? 'preCall.practitionerRequired.description'
              : 'preCall.microphoneRequired.description',
          )}
        </p>
        {!practitioner && <p>{t('preCall.microphoneRequired.camera')}</p>}
        <ModalButton audience={audience} tone="green" primary onClick={onClose}>
          {t('preCall.microphoneRequired.close')}
        </ModalButton>
      </DialogContent>
    </SafeDialog>
  );
};

RequiredDialog.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audience: PropTypes.oneOf(['client', 'practitioner']).isRequired,
};

const MediaToggle = ({ enabled, kind, error, onClick, t }) => (
  <button
    type="button"
    className={`vonage-pre-call__toggle${error ? ' has-error' : ''}`}
    aria-pressed={enabled}
    onClick={onClick}
  >
    {kind === 'camera' ? (
      enabled ? (
        <VideocamOutlined />
      ) : (
        <VideocamOffOutlined />
      )
    ) : enabled ? (
      <MicOutlined />
    ) : (
      <MicOffOutlined />
    )}
    <span>{t(enabled ? 'preCall.turnOff' : 'preCall.turnOn')}</span>
  </button>
);

MediaToggle.propTypes = {
  enabled: PropTypes.bool.isRequired,
  kind: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const TechnicalIssues = ({ appointment, t }) => (
  <div
    className="vonage-pre-call__technical"
    data-testid="pre-call-technical-issues"
  >
    <div className="vonage-pre-call__alert">
      <LightbulbOutlined />
      <span>
        <strong>{t('preCall.technicalIssues.title')}</strong>
        {t('preCall.technicalIssues.description', {
          tollFree: appointment.twilio_toll_free_number,
          pin: appointment.twilio_voice_token,
        })}
      </span>
    </div>
  </div>
);

TechnicalIssues.propTypes = {
  appointment: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

const PreCall = ({
  appointment,
  user,
  t,
  onComplete,
  onCancel,
  audience = 'client',
  footerVariant = 'client',
  deviceOrder = ['camera', 'microphone', 'speaker'],
  mediaController = null,
  showPermissionInstructionInitially = true,
}) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const browserMedia = usePreCallMedia({ audioElement: audioRef });
  const media = mediaController || browserMedia;
  const mediaRef = useRef(media);
  const practitioner = audience === 'practitioner';
  mediaRef.current = media;
  const initialized = useRef(false);
  const [gate, setGate] = useState(
    showPermissionInstructionInitially ? 'checking' : 'pre-call',
  );
  const [showRequired, setShowRequired] = useState(false);
  const [liveCaptionsEnabled, setLiveCaptionsEnabled] = useState(false);
  const liveCaptionsAvailable = Boolean(appointment.live_captions_available);

  useEffect(() => {
    let cancelled = false;
    const initializeOnce = () => {
      if (initialized.current) return;
      initialized.current = true;
      mediaRef.current.initialize();
    };
    if (!showPermissionInstructionInitially) {
      initializeOnce();
      return () => {
        cancelled = true;
      };
    }
    Promise.resolve(mediaRef.current.hasGrantedCameraAndMicrophonePermissions())
      .catch(() => false)
      .then(granted => {
        if (cancelled) return;
        setGate(granted ? 'pre-call' : 'instruction');
        if (granted) initializeOnce();
      });
    return () => {
      cancelled = true;
    };
  }, [showPermissionInstructionInitially]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    video.srcObject = media.cameraStream;
    if (media.cameraStream) video.play().catch(() => {});
    return () => {
      if (video.srcObject === media.cameraStream) video.srcObject = null;
    };
  }, [gate, media.cameraStream]);

  const cancel = () => {
    media.cleanup();
    onCancel();
  };
  const continueToPreview = () => {
    setGate('pre-call');
    if (!initialized.current) {
      initialized.current = true;
      media.initialize();
    }
  };
  const join = () => {
    const current = media.getSettings();
    const cameraReady =
      media.cameraStatus === 'ready' && Boolean(current.cameraDeviceId);
    if (!media.isMicrophoneReady() || (practitioner && !cameraReady)) {
      setShowRequired(true);
      return;
    }
    const mediaSettings =
      media.cameraStatus === 'ready'
        ? current
        : { ...current, cameraDeviceId: undefined, isCameraEnabled: false };
    const settings = {
      ...mediaSettings,
      isLiveCaptionsEnabled: liveCaptionsAvailable && liveCaptionsEnabled,
    };
    updateDevicePreferences({
      microphoneDeviceId: settings.microphoneDeviceId,
      cameraDeviceId: settings.cameraDeviceId,
      speakerDeviceId: settings.speakerDeviceId,
    });
    media.cleanup();
    onComplete(settings);
  };

  if (gate === 'checking') return null;
  if (gate === 'instruction')
    return (
      <PermissionDialog
        t={t}
        onContinue={continueToPreview}
        onDismiss={cancel}
        audience={audience}
      />
    );

  const microphoneError = ['denied', 'missing', 'failed'].includes(
    media.microphoneStatus,
  );
  const cameraError = ['denied', 'missing', 'failed'].includes(
    media.cameraStatus,
  );
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const initials = fullName
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2);

  return (
    <>
      <SafeDialog
        open
        onClose={cancel}
        className={`vonage-pre-call-dialog vonage-pre-call-dialog--${audience}`}
        testId="pre-call"
      >
        <DialogContent
          className={`vonage-pre-call vonage-pre-call--${audience}`}
        >
          <CloseButton onClick={cancel} label={t('preCall.close')} />
          <header>
            <h1>{t('preCall.title')}</h1>
            <p>
              {t(
                practitioner
                  ? 'preCall.practitionerDescription'
                  : 'preCall.description',
              )}
            </p>
          </header>
          <div className="vonage-pre-call__content">
            <div className="vonage-pre-call__preview-column">
              <section className="vonage-pre-call__preview-panel">
                <div className="vonage-pre-call__preview">
                  <strong>{fullName}</strong>
                  <video
                    aria-label={t('preCall.cameraPreview')}
                    autoPlay
                    muted
                    playsInline
                    ref={videoRef}
                    hidden={
                      media.cameraStatus !== 'ready' || !media.isCameraEnabled
                    }
                  />
                  {(media.cameraStatus !== 'ready' ||
                    !media.isCameraEnabled) && (
                    <span className="vonage-pre-call__avatar">{initials}</span>
                  )}
                </div>
                <div className="vonage-pre-call__toggles">
                  <MediaToggle
                    enabled={media.isCameraEnabled}
                    kind="camera"
                    error={cameraError}
                    t={t}
                    onClick={() =>
                      media.cameraStatus === 'ready' &&
                      media.setCameraEnabled(!media.isCameraEnabled)
                    }
                  />
                  <MediaToggle
                    enabled={media.isMicrophoneEnabled}
                    kind="microphone"
                    error={microphoneError}
                    t={t}
                    onClick={() =>
                      media.microphoneStatus === 'ready' &&
                      media.setMicrophoneEnabled(!media.isMicrophoneEnabled)
                    }
                  />
                </div>
                {!practitioner && microphoneError && (
                  <div className="vonage-pre-call__alert is-critical">
                    <InfoOutlined />
                    <span>
                      <strong>
                        {t('preCall.microphoneAccessNeeded.title')}
                      </strong>
                      {t('preCall.microphoneAccessNeeded.description')}
                    </span>
                  </div>
                )}
              </section>
              <TechnicalIssues appointment={appointment} t={t} />
            </div>
            <section className="vonage-pre-call__settings">
              <DeviceSettingsFields
                controller={{ ...media, audioElementRef: audioRef }}
                t={t}
                order={deviceOrder}
              />
              {liveCaptionsAvailable && (
                <LiveCaptionsToggle
                  checked={liveCaptionsEnabled}
                  deviceFieldStyle={!practitioner}
                  onChange={setLiveCaptionsEnabled}
                  t={t}
                />
              )}
            </section>
          </div>
          <footer
            className={`vonage-pre-call__footer vonage-pre-call__footer--${footerVariant}`}
          >
            <ModalButton audience={audience} tone="white" onClick={cancel}>
              {t('preCall.cancel')}
            </ModalButton>
            <ModalButton audience={audience} tone="green" onClick={join}>
              {t('preCall.join')}
            </ModalButton>
          </footer>
        </DialogContent>
      </SafeDialog>
      <RequiredDialog
        t={t}
        open={showRequired}
        onClose={() => setShowRequired(false)}
        audience={audience}
      />
    </>
  );
};

PreCall.propTypes = {
  appointment: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  audience: PropTypes.oneOf(['client', 'practitioner']),
  footerVariant: PropTypes.string,
  deviceOrder: PropTypes.arrayOf(
    PropTypes.oneOf(['camera', 'microphone', 'speaker']),
  ),
  mediaController: PropTypes.object,
  showPermissionInstructionInitially: PropTypes.bool,
};

export default PreCall;
